import { useCallback, useMemo } from 'react';
import { apiRequest } from '../services/apiClient';

/**
 * Centralized mutation client for /api/employees. Returns an object
 * of async functions, one per endpoint. Each rejects on failure with
 * an ApiError carrying the route's `{ issues }` array when present.
 *
 * Callers wrap individual functions in useMutation() (or manage
 * loading/error state directly). The hook intentionally exposes raw
 * mutators rather than per-action useMutation instances so the
 * EmployeeProfilePage can compose optimistic-update logic
 * deterministically — useMutation cannot easily reach the
 * page-level bundle cache.
 */
export function useEmployeeProfileMutations({ profileId } = {}) {
  const profilePath = profileId ? `/api/employees/profiles/${profileId}` : null;

  const updateProfile = useCallback((payload) => (
    apiRequest(profilePath, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    })
  ), [profilePath]);

  const archiveProfile = useCallback(() => (
    apiRequest(`${profilePath}/archive`, { method: 'POST', body: JSON.stringify({}) })
  ), [profilePath]);

  const offboardProfile = useCallback((reason) => (
    apiRequest(`${profilePath}/offboard`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    })
  ), [profilePath]);

  const restoreProfile = useCallback(() => (
    apiRequest(`${profilePath}/restore`, { method: 'POST', body: JSON.stringify({}) })
  ), [profilePath]);

  const createAddress = useCallback((payload) => (
    apiRequest(`${profilePath}/addresses`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  ), [profilePath]);

  const updateAddress = useCallback((addressId, payload) => (
    apiRequest(`/api/employees/addresses/${addressId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    })
  ), []);

  const setPrimaryAddress = useCallback((addressId) => (
    apiRequest(`/api/employees/addresses/${addressId}/primary`, {
      method: 'POST',
      body: JSON.stringify({})
    })
  ), []);

  const deleteAddress = useCallback((addressId) => (
    apiRequest(`/api/employees/addresses/${addressId}`, { method: 'DELETE' })
  ), []);

  const createEmergencyContact = useCallback((payload) => (
    apiRequest(`${profilePath}/emergency-contacts`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  ), [profilePath]);

  const updateEmergencyContact = useCallback((contactId, payload) => (
    apiRequest(`/api/employees/emergency-contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    })
  ), []);

  const deleteEmergencyContact = useCallback((contactId) => (
    apiRequest(`/api/employees/emergency-contacts/${contactId}`, { method: 'DELETE' })
  ), []);

  const upsertPreferences = useCallback((payload) => (
    apiRequest(`${profilePath}/preferences`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
  ), [profilePath]);

  return useMemo(() => ({
    updateProfile,
    archiveProfile,
    offboardProfile,
    restoreProfile,
    createAddress,
    updateAddress,
    setPrimaryAddress,
    deleteAddress,
    createEmergencyContact,
    updateEmergencyContact,
    deleteEmergencyContact,
    upsertPreferences
  }), [
    updateProfile, archiveProfile, offboardProfile, restoreProfile,
    createAddress, updateAddress, setPrimaryAddress, deleteAddress,
    createEmergencyContact, updateEmergencyContact, deleteEmergencyContact,
    upsertPreferences
  ]);
}

/**
 * Pure helper: turn the route's 400 `issues` array into a per-field
 * { fieldName: message } object the modal forms can render.
 */
export function issuesToFieldErrors(issues) {
  if (!Array.isArray(issues)) return {};
  const out = {};
  for (const issue of issues) {
    const key = issue?.path || '_form';
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}
